import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { isStockRequired } from '../../../../utils/stock';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  
  // Default product image (same placeholder used in caisse / POS)
  const defaultProductImage = "/logo.jpg";

  // Normalize fields coming from different API shapes
  const getFirstValidImage = (arr) => {
    if (!Array.isArray(arr)) return null;
    for (const i of arr) {
      if (i && String(i).trim() !== '' && i !== 0 && i !== '0') return i;
    }
    return null;
  };
  const firstImage = getFirstValidImage(product.imageUrl);
  const imageSrc = product.image && String(product.image).trim() ? product.image : (firstImage || defaultProductImage);
  const rawPrice = product.price ?? product.prixVenteTTC ?? product.prixVenteHt ?? 0;
  const promoPrice = product.prixVentePromo ?? product.PrixVentePromo ?? product.prixVentePromo ?? 0;
  const parseBool = (v) => v === true || v === 'true' || v === 1 || v === '1';
  // prefer mapped fields from viewmodel if present
  const finalPromoPrice = product.prixVentePromo ?? product.prixVentePromo ?? promoPrice;
  const finalEnPromo = product.enPromo ?? product.enPromo ?? product.enPromo;
  const promoActive = parseBool(finalEnPromo) || (Number(finalPromoPrice) > 0);

  // Product card animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    hover: {
      y: -5,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  // Button animation variants
  const buttonVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        delay: 0.1,
        duration: 0.3
      }
    }
  };

  // Image animation variants
  const imageVariants = {
    hover: { 
      scale: 1.08,
      transition: { duration: 0.5 }
    }
  };

  // Badge animation variants
  const badgeVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        delay: 0.2,
        duration: 0.3,
        type: "spring",
        stiffness: 300
      }
    }
  };

  // Format price with currency
  const formatPrice = (price) => {
    const n = Number(price) || 0;
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2
    }).format(n);
  };

  // Handle product click
  const handleProductClick = (e) => {
    e.preventDefault();
    navigate(`/product/${product.id}/0`);
  };

  // Handle image load
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Check if product is new (less than 14 days old)
  const isNewProduct = () => {
    if (!product.createdAt) return false;
    const productDate = new Date(product.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - productDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 14;
  };

  // Check if product has discount
  const hasDiscount = () => promoActive;

  // Calculate discount percentage
  const getDiscountPercentage = () => {
    if (!hasDiscount()) return 0;
    const base = Number(rawPrice) || 0;
    const promo = Number(promoPrice) || 0;
    if (base <= 0 || promo <= 0) return 0;
    return Math.round(((base - promo) / base) * 100);
  };

  // Handle quick view button
  const handleQuickView = (e) => {
    e.stopPropagation();
    setShowQuickView(true);
  };

  // Handle add to cart button
  const handleAddToCart = (e) => {
    e.stopPropagation();
    // Add your cart logic here
    
    // Show success feedback
    const successElement = document.createElement('div');
    successElement.className = 'fixed z-50 top-5 right-5 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-lg transition-all duration-500 transform translate-x-full';
    successElement.innerHTML = `
      <div class="flex items-center">
        <svg class="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
        </svg>
        <span>${product.name} ajouté au panier</span>
      </div>
    `;
    document.body.appendChild(successElement);
    
    // Animate in
    setTimeout(() => {
      successElement.classList.remove('translate-x-full');
    }, 10);
    
    // Animate out and remove
    setTimeout(() => {
      successElement.classList.add('translate-x-full');
      setTimeout(() => {
        document.body.removeChild(successElement);
      }, 500);
    }, 3000);
  };

  // Custom wave effect on button click
  const createRipple = (event) => {
    const button = event.currentTarget;
    
    const circle = document.createElement("span");
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;
    
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${event.clientX - button.offsetLeft - radius}px`;
    circle.style.top = `${event.clientY - button.offsetTop - radius}px`;
    circle.classList.add("ripple");
    
    const ripple = button.getElementsByClassName("ripple")[0];
    
    if (ripple) {
      ripple.remove();
    }
    
    button.appendChild(circle);
  };

  return (
    <motion.div 
      className="relative"
      initial="hidden"
      animate="visible"
      whileHover="hover"
      variants={cardVariants}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Card content with premium design */}
      <div className="group relative rounded-xl overflow-hidden shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 bg-white h-full flex flex-col">
        {/* Image container */}
        <div 
          className="relative overflow-hidden"
          style={{ height: '260px' }}
          onClick={handleProductClick}
        >
          {/* Loading skeleton */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-100 animate-pulse"></div>
          )}
          
          {/* Product image with zoom hover effect */}
          <motion.img
            src={imageSrc}
            alt={product.name}
            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = defaultProductImage;
            }}
            onLoad={handleImageLoad}
            variants={imageVariants}
          />
          
          {/* Overlay gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          
          {/* Quick view and cart buttons on hover */}
          <AnimatePresence>
            {isHovered && (
              <motion.div 
                className="absolute bottom-0 left-0 right-0 flex justify-center p-4 gap-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
              >
                <button 
                  onClick={handleQuickView}
                  className="bg-white/90 backdrop-blur-sm text-gray-800 hover:bg-white hover:text-[#174540] py-2 px-3 rounded-full shadow-md text-xs font-medium transition-all duration-300 flex items-center"
                  onMouseDown={createRipple}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Aperçu rapide
                </button>
                
                
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* New product badge */}
          {isNewProduct() && (
            <motion.div 
              className="absolute top-3 left-3 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-md shadow-lg z-10"
              variants={badgeVariants}
              initial="hidden"
              animate="visible"
            >
              NOUVEAU
            </motion.div>
          )}
          
          {/* Discount badge */}
          {hasDiscount() && (
            <motion.div 
              className="absolute top-3 right-3 bg-rose-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-md shadow-lg z-10"
              variants={badgeVariants}
              initial="hidden"
              animate="visible"
            >
              -{getDiscountPercentage()}%
            </motion.div>
          )}
        </div>
        
        {/* Product info */}
        <div className="p-4 flex-grow flex flex-col justify-between">
          <div>
            {/* Brand */}
            <div className="flex justify-between items-start mb-1">
                <span className="text-xs text-[#174540] font-medium uppercase tracking-wider">
                  {product.brand?.name || product.marque?.name || product.category || "Premium"}
                </span>
              
              {/* Rating */}
              <div className="flex items-center">
                <span className="text-xs font-semibold text-gray-700 mr-1">
                  {product.rating?.toFixed(1) || "4.5"}
                </span>
                <svg className="h-3.5 w-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            </div>
            
            {/* Product name */}
            <h3 className="font-medium text-gray-900 line-clamp-2 h-12 mb-2">
              <a 
                href={`/product/${product.id}/0`}
                onClick={handleProductClick}
                className="hover:text-[#174540] transition-colors duration-200"
              >
                {product.name}
              </a>
            </h3>
          </div>
          
          {/* Price area */}
          <div className="mt-auto">
            <div className="flex items-end justify-between">
              <div>
                {hasDiscount() && (
                  <span className="text-xs text-gray-500 line-through mr-2">
                    {formatPrice(rawPrice)}
                  </span>
                )}
                <span className="text-lg font-bold text-gray-900">
                  {formatPrice(hasDiscount() ? promoPrice : rawPrice)}
                </span>
              </div>
              
              {/* Wishlist button */}
              <button className="p-1.5 rounded-full hover:bg-gray-100 transition-colors duration-200">
                <svg className="w-5 h-5 text-gray-400 hover:text-rose-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </div>
            
            {/* Stock indicator - shows only if stock is low, never for products sold without stock */}
            {isStockRequired(product) && product.stock > 0 && product.stock < 5 && (
              <div className="mt-2 flex items-center">
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                <span className="ml-2 text-xs text-red-600 font-medium">
                  {product.stock === 1 ? "Dernier article!" : `Plus que ${product.stock} en stock`}
                </span>
              </div>
            )}
            
            {/* View product button */}
            <motion.button
              onClick={handleProductClick}
              className="mt-3 w-full relative overflow-hidden bg-gradient-to-r from-[#174540] to-[#1e5853] text-white py-2 px-4 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center group"
              variants={buttonVariants}
              onMouseDown={createRipple}
            >
              <span className="group-hover:translate-x-[-8px] transition-transform duration-300">
                Voir le produit
              </span>
              <svg 
                className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transform translate-x-[-16px] group-hover:translate-x-0 transition-all duration-300" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </motion.button>
          </div>
        </div>
        
        {/* Remove the QuickAddButton component */}
        {/* 
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <QuickAddButton product={product} />
        </div>
        */}
      </div>

      {/* Global styles for ripple effect */}
      <style jsx>{`
        .ripple {
          position: absolute;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          transform: scale(0);
          animation: ripple 0.6s linear;
          pointer-events: none;
        }

        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default ProductCard;